import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import {
  activateServerWorld, destroyServerWorld, fetchServerWorldDownload,
  fetchServerWorlds, resetServerWorld, syncServerContentJob, uploadServerWorld
} from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";
import usePermissions from "src/hooks/usePermissions";
import { formattedDate } from "src/helpers/DateHelpers";

import { Button, Input, Modal, Popconfirm, Switch, Tag, message } from 'antd';
import {
  CloudUploadOutlined, DownloadOutlined, ExclamationCircleFilled,
  GlobalOutlined, InboxOutlined, SettingOutlined, SyncOutlined
} from '@ant-design/icons';
import Card from "src/componets/Card";
import WorldSettings from "./_WorldSettings";
import { shortFromNow } from "./_serverHelpers";

interface WorldEntry {
  name: string;
  active: boolean;
  modifiedAt: string | null;
}

// Upload finishes through the shared content-job pipeline; poll the job
// at the Content tab's cadence until it settles, then re-read the list.
const UPLOAD_POLL_MS = 10_000;

const _Worlds = ({ server }: { server: ServerInterface | undefined }) => {
  const dispatch = useAppDispatch();
  const { write_minecraft } = usePermissions();
  const hosted = server?.hosted_server;

  const [worlds, setWorlds] = useState<WorldEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [activateOnInstall, setActivateOnInstall] = useState(false);
  const [uploadingJob, setUploadingJob] = useState<number | null>(null);

  const [settingsWorld, setSettingsWorld] = useState<string | null>(null);

  // A ref, not state: Modal.confirm captures its onOk once, so a state
  // value would be stale by the time the user confirms.
  const resetSeed = useRef('');
  const resetConfirmName = useRef('');

  const serverId = server?.id;
  const ready = hosted?.state === 'ready';

  const loadWorlds = useCallback(async (silent = false) => {
    if (!serverId) return;

    if (!silent) setLoading(true);
    const resultAction = await dispatch(fetchServerWorlds(serverId));
    if (!silent) setLoading(false);

    if (fetchServerWorlds.fulfilled.match(resultAction)) {
      setWorlds(resultAction.payload.worlds || []);
    }
  }, [serverId, dispatch]);

  useEffect(() => {
    if (hosted) loadWorlds().catch(console.error);
  }, [hosted, loadWorlds]);

  // While an upload job is pending, sync it on an interval; a terminal
  // state reports the outcome and refreshes the world list.
  useEffect(() => {
    if (!serverId || uploadingJob === null) return;

    const timer = setInterval(async () => {
      const resultAction = await dispatch(syncServerContentJob({ id: serverId, jobId: uploadingJob }));

      if (!syncServerContentJob.fulfilled.match(resultAction)) return;

      const job = resultAction.payload?.content_job;
      if (!job || job.state === 'queued' || job.state === 'pending') return;

      setUploadingJob(null);

      if (job.state === 'installed') {
        message.success('World installed');
        loadWorlds(true).catch(console.error);
      } else {
        message.error(job.error || 'World install failed');
      }
    }, UPLOAD_POLL_MS);

    return () => clearInterval(timer);
  }, [serverId, uploadingJob, dispatch, loadWorlds]);

  if (!server || !hosted) {
    return (
      <Card>
        <div className='v-srv-empty'>
          <InboxOutlined className='o-icon' />
          <div className='o-title'>Not hosted through crafty-servers</div>
          <div className='o-hint'>Worlds can only be managed on servers we host.</div>
        </div>
      </Card>
    );
  }

  function fail(payload: any, fallback: string) {
    message.error(Array.isArray(payload) ? payload[0] : fallback);
  }

  const canUpload = !!url.trim() && !!name.trim() && !uploadingJob;

  async function upload() {
    if (!serverId || !canUpload) return;

    setBusy('upload');
    const resultAction = await dispatch(uploadServerWorld({
      id:       serverId,
      url:      url.trim(),
      name:     name.trim(),
      activate: activateOnInstall || undefined
    }));
    setBusy(null);

    if (uploadServerWorld.fulfilled.match(resultAction)) {
      const job = resultAction.payload?.content_job;

      // 202 even when the hosting service refused the upload — the
      // refusal comes back on the row, so there is nothing to poll.
      if (job?.state === 'failed') {
        message.error(job.error || 'Upload rejected');
        return;
      }

      message.success('World queued — installing in the background');
      setUrl('');
      setName('');
      setActivateOnInstall(false);
      setUploadingJob(job?.id || null);
    } else {
      fail(resultAction.payload, 'Upload rejected');
    }
  }

  async function activate(world: WorldEntry) {
    if (!serverId) return;

    setBusy(`activate-${world.name}`);
    const resultAction = await dispatch(activateServerWorld({ id: serverId, world: world.name }));
    setBusy(null);

    if (activateServerWorld.fulfilled.match(resultAction)) {
      const restarted = resultAction.payload?.restarted;
      message.success(`${world.name} is now the active world${restarted ? ' — server restarting' : ''}`);
      loadWorlds(true).catch(console.error);
    } else {
      fail(resultAction.payload, 'Activation failed');
    }
  }

  async function download(world: WorldEntry) {
    if (!serverId) return;

    setBusy(`download-${world.name}`);
    const resultAction = await dispatch(fetchServerWorldDownload({ id: serverId, world: world.name }));
    setBusy(null);

    if (fetchServerWorldDownload.fulfilled.match(resultAction) && resultAction.payload?.url) {
      window.open(resultAction.payload.url, '_blank', 'noopener');
    } else {
      fail(resultAction.payload, 'Download link failed');
    }
  }

  async function remove(world: WorldEntry) {
    if (!serverId) return;

    const resultAction = await dispatch(destroyServerWorld({ id: serverId, world: world.name }));

    if (destroyServerWorld.fulfilled.match(resultAction)) {
      message.success(`${world.name} deleted`);
      loadWorlds(true).catch(console.error);
    } else {
      fail(resultAction.payload, 'Delete failed');
    }
  }

  // The single most destructive action on this screen: typing the world
  // name arms the OK button — stronger friction than any confirm here.
  function confirmReset(world: WorldEntry) {
    if (!serverId) return;

    resetSeed.current = '';
    resetConfirmName.current = '';

    const modal = Modal.confirm({
      title: `Reset ${world.name}?`,
      icon: <ExclamationCircleFilled />,
      width: 460,
      okText: 'Reset world',
      okButtonProps: { danger: true, disabled: true },
      content: (
        <div className='v-srv-reset'>
          <p>
            The current world is <b>deleted</b> and a fresh one generates in its place.
            This cannot be undone — download the world or create a backup first.
          </p>
          <Input
            placeholder='New seed (optional)'
            maxLength={64}
            onChange={event => { resetSeed.current = event.target.value }}
          />
          <Input
            placeholder={`Type "${world.name}" to confirm`}
            maxLength={64}
            onChange={event => {
              resetConfirmName.current = event.target.value;
              modal.update({ okButtonProps: { danger: true, disabled: event.target.value !== world.name } });
            }}
          />
        </div>
      ),
      onOk: async () => {
        if (resetConfirmName.current !== world.name) return;

        const resultAction = await dispatch(resetServerWorld({
          id:   serverId,
          seed: resetSeed.current.trim() || undefined
        }));

        if (resetServerWorld.fulfilled.match(resultAction)) {
          message.success('World resetting — the server restarts with a fresh world');
          loadWorlds(true).catch(console.error);
        } else {
          fail(resultAction.payload, 'Reset failed');
        }
      }
    });
  }

  return (
    <>
      {write_minecraft &&
        <Card className='v-srv-world-upload'>
          <h4 className='v-content-title'>Install a world</h4>

          <div className='o-row'>
            <Input
              className='o-url'
              placeholder='https://… (.mcworld or .zip with level.dat)'
              value={url}
              disabled={busy === 'upload'}
              onChange={event => setUrl(event.target.value)}
            />
            <Input
              className='o-name'
              placeholder='World name'
              maxLength={64}
              value={name}
              disabled={busy === 'upload'}
              onChange={event => setName(event.target.value)}
            />
            <label className='o-activate'>
              <Switch
                size='small'
                checked={activateOnInstall}
                onChange={setActivateOnInstall}
              />
              <span>Activate after install</span>
            </label>
            <Button
              type='primary'
              icon={<CloudUploadOutlined />}
              loading={busy === 'upload'}
              disabled={!ready || !canUpload}
              onClick={upload}
            >
              Install
            </Button>
          </div>

          <div className='o-hint'>
            {uploadingJob
              ? 'A world is installing — progress also shows under Content → install jobs.'
              : 'Installs as a NEW world next to the others, without touching the active one.'}
          </div>
        </Card>
      }

      <Card className='v-srv-worlds'>
        <div className='o-head'>
          <h4 className='v-content-title'>Worlds</h4>
          <span className='o-count'>{worlds.length}</span>
          <Button
            className='o-icon-btn'
            icon={<SyncOutlined />}
            title='Refresh'
            aria-label='Refresh worlds'
            loading={loading}
            onClick={() => loadWorlds()}
          />
        </div>

        <div className='o-list'>
          {worlds.map(world => (
            <div key={world.name} className='o-world'>
              <GlobalOutlined className='o-globe' />

              <div className='o-names'>
                <div className='o-name'>
                  {world.name}
                  {world.active && <Tag color='green'>active</Tag>}
                </div>
                {world.modifiedAt &&
                  <div className='o-sub' title={formattedDate(world.modifiedAt).dateTime}>
                    modified {shortFromNow(world.modifiedAt)}
                  </div>}
              </div>

              <div className='o-actions'>
                {write_minecraft && !world.active &&
                  <Popconfirm
                    title={`Make ${world.name} the active world? The server restarts if running.`}
                    onConfirm={() => activate(world)}
                  >
                    <Button size='small' loading={busy === `activate-${world.name}`}>
                      Activate
                    </Button>
                  </Popconfirm>
                }

                <Button
                  size='small'
                  icon={<SettingOutlined />}
                  onClick={() => setSettingsWorld(world.name)}
                >
                  Settings
                </Button>

                <Button
                  size='small'
                  icon={<DownloadOutlined />}
                  loading={busy === `download-${world.name}`}
                  onClick={() => download(world)}
                >
                  Download
                </Button>

                {write_minecraft && world.active &&
                  <Button size='small' danger onClick={() => confirmReset(world)}>
                    Reset
                  </Button>
                }

                {write_minecraft && !world.active &&
                  <Popconfirm
                    title={`Delete ${world.name}? This cannot be undone — download it first for a copy.`}
                    okButtonProps={{ danger: true }}
                    onConfirm={() => remove(world)}
                  >
                    <Button size='small' danger>Delete</Button>
                  </Popconfirm>
                }
              </div>
            </div>
          ))}
        </div>

        {!loading && !worlds.length &&
          <div className='v-srv-empty'>
            <InboxOutlined className='o-icon' />
            <div className='o-title'>No worlds listed</div>
            <div className='o-hint'>
              {ready
                ? 'The server\'s /worlds folder is empty — install one above.'
                : 'Worlds appear once the server is provisioned.'}
            </div>
          </div>
        }
      </Card>

      <WorldSettings
        server={server}
        world={settingsWorld}
        active={worlds.find(world => world.name === settingsWorld)?.active || false}
        onClose={() => setSettingsWorld(null)}
      />
    </>
  );
}

export default _Worlds;
